import { useId, useState } from 'react'
import { TIMELINE_STEPS } from '../utils/cardFlowConfig'
import { formatDateTimeOrNull } from '../utils/formatters'
import { STEP_ICONS, DEFAULT_STEP_ICON } from '../utils/timelineIcons'

function safeStepDate(dates, key) {
  const raw = dates[key]
  if (raw == null) return null
  return formatDateTimeOrNull(raw)
}

/** Round marker of one stage: filled check when completed, outlined icon when current or pending. */
function StepMarker({ state, iconName, label }) {
  if (state === 'completed') {
    return (
      <span
        className="relative z-[1] flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-mk-line bg-[#47bb64] text-white"
        title={label}
      >
        <i className="ri-check-line text-base leading-none" aria-hidden="true" />
        <span className="sr-only">Completed</span>
      </span>
    )
  }
  if (state === 'current') {
    return (
      <span
        className="relative z-[1] flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[#47bb64] bg-mk-raised text-[#47bb64]"
        title={label}
      >
        <i className={`${iconName} text-sm leading-none`} aria-hidden="true" />
        <span className="sr-only">In progress</span>
      </span>
    )
  }
  return (
    <span
      className="relative z-[1] flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-mk-line bg-mk-primary text-mk-muted"
      title={label}
    >
      <i className={`${iconName} text-sm leading-none`} aria-hidden="true" />
      <span className="sr-only">Pending</span>
    </span>
  )
}

/**
 * Vertical timeline of the grading journey, laid out like a marketplace mint schedule: a round
 * marker per stage joined by a hairline, then the stage name (with a small chip for the current
 * one) and one muted line with its date and "step N of M".
 *
 * It starts collapsed on the most recent stages (`collapsedCount`, ending at the current one), so
 * the card page stays short. "Show all steps" reveals the full journey, including stages still ahead.
 */
export default function GradingHistoryTimeline({
  currentStep = 0,
  steps = TIMELINE_STEPS,
  dates = {},
  collapsedCount = 3,
}) {
  const [expanded, setExpanded] = useState(false)
  const listId = useId()
  const total = steps.length

  // Index of the stage the order is at now (clamped to the journey).
  const currentIndex = Math.min(Math.max(currentStep, 1), total) - 1
  const canCollapse = total > collapsedCount
  const collapsed = canCollapse && !expanded
  const firstIndex = collapsed ? Math.max(0, currentIndex - (collapsedCount - 1)) : 0
  const lastIndex = collapsed ? currentIndex : total - 1
  const hiddenCount = total - (lastIndex - firstIndex + 1)

  return (
    <div className="w-full font-sans">
      <ol id={listId} className="w-full">
        {steps.slice(firstIndex, lastIndex + 1).map((step, offset) => {
          const index = firstIndex + offset
          const stepNumber = index + 1
          const isLast = index === lastIndex
          const isFinalStage = index === total - 1
          const dateLine = safeStepDate(dates, step.key)
          // The final stage is terminal: once it has a date it counts as completed.
          const state =
            currentStep > stepNumber ||
            (isFinalStage && currentStep === stepNumber && Boolean(dateLine))
              ? 'completed'
              : currentStep === stepNumber
                ? 'current'
                : 'pending'
          const iconName = STEP_ICONS[step.key] || DEFAULT_STEP_ICON

          return (
            <li
              key={step.key}
              className={`relative flex items-start gap-4 ${isLast ? '' : 'pb-4'}`}
            >
              {!isLast && (
                <span
                  className="absolute bottom-0 left-3 top-6 w-0 border-l border-mk-line"
                  aria-hidden="true"
                />
              )}
              <StepMarker state={state} iconName={iconName} label={step.label} />
              <div className="flex min-w-0 flex-col">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`text-sm font-medium leading-normal ${
                      state === 'current' ? 'text-white' : 'text-mk-muted'
                    }`}
                  >
                    {step.label}
                  </span>
                  {state === 'current' && (
                    <span className="flex w-fit shrink-0 items-center rounded bg-[#3c3d40] px-1.5 py-0.5 text-xs font-medium text-mk-muted">
                      In progress
                    </span>
                  )}
                </div>
                <span className="text-[13px] leading-normal text-mk-muted">
                  {dateLine || (state === 'pending' ? 'Not started yet' : 'Date not recorded')}
                  <span className="font-m1-mono text-[11px] uppercase text-mk-muted/70">
                    {' · '}Step {stepNumber} of {total}
                  </span>
                </span>
              </div>
            </li>
          )
        })}
      </ol>

      {canCollapse && (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          aria-controls={listId}
          className="mt-3 inline-flex min-h-[36px] items-center gap-1.5 text-[13px] font-medium text-white/80 transition-colors hover:text-white"
        >
          {expanded ? 'Show fewer steps' : `Show all ${total} steps`}
          <i
            className={`ri-arrow-down-s-line text-base leading-none transition-transform ${
              expanded ? 'rotate-180' : ''
            }`}
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  )
}
